import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, MessageCircle, QrCode, RotateCcw } from 'lucide-react';
import PageHeader from '../../components/site/PageHeader';
import SEO from '../../components/SEO';
import { seoConfig } from '../../configs/seoConfig';
import contactInfo, { getHoursSentence } from '../../configs/contactInfo';
import { useSiteConfig } from '../../configs/siteConfig';
import { categoriesAPI, productsAPI } from '../../api/apiService';
import { getAllReviews } from '../../services/reviewService';
import { getErrorMessage } from '../../utils/apiError';
import './AboutPage.css';

const FACTS = [
  {
    icon: QrCode,
    title: 'Pay by UPI',
    text: 'Scan the QR code at checkout with any UPI app. Every payment is checked by hand.',
  },
  {
    icon: Clock,
    title: 'Access, typically within 12 hours',
    text: 'Once your payment is verified, open your project from your order page: GitHub repository access and/or downloadable files.',
  },
  {
    icon: MessageCircle,
    // Getters: the hours come from Admin → Settings and can change while the page is open.
    get title() {
      return contactInfo.businessHours.weekdays.every(day => day >= 1 && day <= 5)
        ? 'Support on weekdays'
        : 'Support by WhatsApp and phone';
    },
    get text() {
      return `Questions before or after you buy? We answer on WhatsApp and phone, ${getHoursSentence()}.`;
    },
  },
];

const formatCount = value => Number(value).toLocaleString('en-IN');

// Real numbers from the API, so this page never shows invented stats.
const loadStats = async () => {
  const [products, categories, reviews] = await Promise.all([
    productsAPI.getAll({ itemsPerPage: 1 }),
    categoriesAPI.getAll({ pageSize: 100, isActive: true }),
    getAllReviews(),
  ]);

  const reviewList = Array.isArray(reviews?.data) ? reviews.data : [];
  const ratings = reviewList
    .map(review => Number(review.rating))
    .filter(rating => Number.isFinite(rating) && rating > 0);
  const average = ratings.length
    ? ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length
    : 0;

  return [
    {
      label: 'Projects',
      value: Number(products?.data?.data?.totalCount) || 0,
      to: '/products',
    },
    {
      label: 'Categories',
      value: Array.isArray(categories?.data?.data)
        ? categories.data.data.length
        : 0,
    },
    { label: 'Customer reviews', value: ratings.length, to: '/reviews' },
    {
      label: 'Average rating',
      value: average,
      display: `${average.toFixed(1)} / 5`,
      to: '/reviews',
    },
  ].filter(stat => stat.value > 0);
};

const Stats = () => {
  const [state, setState] = useState({ status: 'loading', stats: [] });

  const load = useCallback(async () => {
    setState({ status: 'loading', stats: [] });
    try {
      setState({ status: 'ready', stats: await loadStats() });
    } catch (error) {
      setState({
        status: 'error',
        stats: [],
        message: getErrorMessage(error, "We couldn't load the numbers."),
      });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (state.status === 'loading') {
    return (
      <div className="ab-stats" aria-busy="true" aria-label="Loading numbers">
        {[0, 1, 2, 3].map(i => (
          <div key={i} className="ab-stat">
            <span className="pk-skel ab-skel-label" />
            <span className="pk-skel ab-skel-value" />
          </div>
        ))}
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="pk-alert ab-stats-error" role="alert">
        <span>{state.message}</span>
        <button
          type="button"
          className="pk-btn pk-btn--secondary pk-btn--xs"
          onClick={load}
        >
          <RotateCcw aria-hidden="true" />
          Retry
        </button>
      </div>
    );
  }

  if (state.stats.length === 0) return null;

  return (
    <dl className="ab-stats">
      {state.stats.map(stat => (
        <div key={stat.label} className="ab-stat">
          <dt>{stat.label}</dt>
          <dd>
            {stat.to ? (
              <Link to={stat.to}>{stat.display || formatCount(stat.value)}</Link>
            ) : (
              stat.display || formatCount(stat.value)
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
};

const AboutPage = () => {
  useSiteConfig();
  return (
    <main className="pk-page">
      <SEO {...seoConfig.pages.about} />
      <div className="pk-container">
        <PageHeader
          title={`About ${contactInfo.companyName}`}
          subtitle="We sell ready-made software projects with source code: web, mobile, machine learning, data science, IoT, blockchain and desktop apps. Pick one, pay by UPI, and get access from your order page."
          crumbs={[{ label: 'Home', to: '/' }, { label: 'About' }]}
        />

        <section className="pk-section" aria-label="Projects Kart in numbers">
          <Stats />
        </section>

        <section className="pk-section" aria-labelledby="ab-how">
          <h2 id="ab-how" className="pk-title-2">
            How buying works
          </h2>
          <div className="pk-grid pk-grid--3">
            {FACTS.map(({ icon: Icon, title, text }) => (
              <div key={title} className="pk-card ab-fact">
                <span className="ab-fact__icon" aria-hidden="true">
                  <Icon />
                </span>
                <h3 className="pk-title-3">{title}</h3>
                <p className="pk-muted">{text}</p>
              </div>
            ))}
          </div>
          <p className="pk-muted ab-note">
            All sales are final. Please read the{' '}
            <Link className="pk-link" to="/refund">
              refund policy
            </Link>{' '}
            and the{' '}
            <Link className="pk-link" to="/terms">
              terms
            </Link>{' '}
            before you buy.
          </p>
        </section>

        <section className="pk-section" aria-labelledby="ab-who">
          <div className="pk-card pk-card--muted ab-who">
            <div>
              <h2 id="ab-who" className="pk-title-2">
                Who runs it
              </h2>
              <p className="pk-muted">
                {contactInfo.companyName} is run by {contactInfo.owner.name}, from{' '}
                {contactInfo.address.city}, {contactInfo.address.country}.
              </p>
            </div>
            <div className="pk-cluster">
              <Link className="pk-btn pk-btn--primary" to="/products">
                Browse projects
              </Link>
              <Link className="pk-btn pk-btn--secondary" to="/contact">
                Contact us
              </Link>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
};

export default AboutPage;
